import {
  Badge,
  Button,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuTrigger,
  MenuSeparator,
  Tooltip,
  cn,
} from '@provenance/design';
import { UserRole } from '@provenance/shared';
import {
  AlertTriangle,
  Building2,
  ChevronDown,
  Database,
  FileCheck2,
  FileStack,
  FileClock,
  FileCheck,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Search,
  LogOut,
  PenLine,
  PenTool,
  Mail,
  Plug,
  ScrollText,
  ShieldCheck,
  Stamp,
  UserCircle2,
  UserPlus,
} from 'lucide-react';
import { type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../lib/session';
import { useCounts } from '../features/counts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { RELEASE_LABEL } from '../release';

/**
 * The console shell.
 *
 * Routed pages with a persistent left rail, replacing the legacy system's ExtJS "open
 * tabs" desktop metaphor (spec §6). Each nav item is a route, not a tab that has to be
 * manually closed — which is both more standard and considerably more accessible.
 *
 * The rail leads with the CHAMBER's identity, not Provenir's. Provenir appears once,
 * small, at the bottom. That is the white-label requirement in §5, and it is why the
 * logo, the name and the accent colour all come from the session's chamber record.
 */

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Which count from the dashboard to show as a badge, if any. */
  count?: 'awaitingAction' | 'awaitingClarification' | 'registrations' | 'modifications' | 'quarantine';
  roles?: readonly string[];
  end?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
  roles?: readonly string[];
}

const CHAMBER_ROLES = [UserRole.CHAMBER_ADMIN, UserRole.CHAMBER_OPERATOR] as const;
/** Chamber screens, plus Provenir Support working inside a chamber (D-031). */
const CHAMBER_WORK = [...CHAMBER_ROLES, UserRole.PLATFORM_SUPPORT] as const;

const SECTIONS: NavSection[] = [
  {
    label: 'Every day',
    items: [
      { to: '/', label: 'Dashboard', icon: <LayoutDashboard className="size-4" />, end: true },
      { to: '/documents/pending', label: 'Pending', icon: <Inbox className="size-4" />, count: 'awaitingAction' },
      {
        to: '/documents/clarification',
        label: 'Awaiting clarification',
        icon: <FileClock className="size-4" />,
        count: 'awaitingClarification',
      },
      { to: '/documents/processed', label: 'Processed', icon: <FileCheck2 className="size-4" /> },
    ],
    roles: CHAMBER_WORK,
  },
  {
    label: 'Requests',
    items: [
      { to: '/registrations', label: 'Registrations', icon: <UserPlus className="size-4" />, count: 'registrations' },
      // Everything that arrived by email, and what became of it (T-136). Replaces Held
      // email: held messages are one of its views.
      { to: '/mailbox', label: 'Mailbox', icon: <Mail className="size-4" />, count: 'quarantine' },
      { to: '/exporters', label: 'Exporters', icon: <Building2 className="size-4" /> },
      {
        to: '/modifications',
        label: 'Modifications',
        icon: <ScrollText className="size-4" />,
        count: 'modifications',
      },
    ],
    roles: CHAMBER_WORK,
  },
  {
    // "I sent it and it vanished" (T-145): one search, then the timeline.
    label: 'Help',
    items: [
      { to: '/support-desk', label: 'Find anything', icon: <Search className="size-4" /> },
      { to: '/support/integrators', label: 'Integrators', icon: <Plug className="size-4" />, roles: [UserRole.PLATFORM_SUPPORT] },
    ],
    roles: [UserRole.CHAMBER_ADMIN, UserRole.PLATFORM_SUPPORT],
  },
  {
    // Chamber Admin only, as a section. Seals, stamping layouts and certificate numbering
    // decide what every certificate this chamber issues looks like and what number it
    // carries; an operator issues certificates under that configuration rather than
    // setting it. The API enforces the same rule — this only stops the console offering
    // a door that answers 403.
    label: 'Configuration',
    items: [
      { to: '/configuration', label: 'Ready to issue?', icon: <ShieldCheck className="size-4" />, end: true },
      { to: '/configuration/seals', label: 'Seal library', icon: <Stamp className="size-4" /> },
      { to: '/configuration/stamping', label: 'Stamping', icon: <PenTool className="size-4" /> },
      { to: '/configuration/required-documents', label: 'Required documents', icon: <FileCheck className="size-4" /> },
      { to: '/configuration/numbering', label: 'Certificate numbers', icon: <FileCheck2 className="size-4" /> },
      { to: '/configuration/master-data', label: 'Master data', icon: <Database className="size-4" /> },
      { to: '/configuration/users', label: 'Operators', icon: <UserCircle2 className="size-4" /> },
      { to: '/configuration/mailbox', label: 'Mailbox settings', icon: <Mail className="size-4" /> },
    ],
    roles: [UserRole.CHAMBER_ADMIN, UserRole.PLATFORM_SUPPORT],
  },
  {
    // An operator's own signature is NOT configuration. It is a personal attestation, and
    // without one they cannot approve a certificate at all — so it stays reachable for
    // everyone, in its own place rather than behind an admin-only section.
    label: 'You',
    items: [
      { to: '/profile/signature', label: 'My signature', icon: <PenLine className="size-4" /> },
    ],
    roles: CHAMBER_ROLES,
  },
  {
    label: 'Platform',
    items: [
      { to: '/platform/chambers', label: 'Chambers', icon: <Building2 className="size-4" /> },
      { to: '/platform/document-types', label: 'Document types', icon: <FileStack className="size-4" /> },
      { to: '/platform/integrators', label: 'Integrators', icon: <Plug className="size-4" /> },
      { to: '/platform/email', label: 'Email', icon: <Mail className="size-4" /> },
      { to: '/platform/admins', label: 'Administrators', icon: <ShieldCheck className="size-4" /> },
      { to: '/platform/support-officers', label: 'Support officers', icon: <LifeBuoy className="size-4" /> },
    ],
    roles: [UserRole.PLATFORM_SUPER_ADMIN],
  },
];

export function AppShell() {
  const { user, signOut, themeWarning, chooseChamber } = useSession();
  const counts = useCounts();
  // Separate from useCounts because it is chamber-only and cheap; folding it into the
  // dashboard counts would make the platform console fetch something it cannot see.
  const quarantineCount = useQuery({
    queryKey: ['mailbox-count'],
    queryFn: async () => ({ count: (await api.get<{ unread: number }>('/api/console/mailbox/count')).unread }),
    enabled: Boolean(user?.chamberId),
    refetchInterval: 60_000,
  });
  const location = useLocation();
  const navigate = useNavigate();

  if (!user) return null;

  // Support with no chamber chosen has nothing to navigate to yet: just the picker (D-031).
  const choosingChamber = user.role === UserRole.PLATFORM_SUPPORT && !user.chamberId;
  const visibleSections = SECTIONS.filter(
    (section) => !choosingChamber && (!section.roles || section.roles.includes(user.role)),
  ).map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.roles || item.roles.includes(user.role)),
  }));

  const countFor = (key: NavItem['count']): number | undefined => {
    if (!key || !counts.data) return undefined;
    const value = {
      awaitingAction: counts.data.awaitingAction.total,
      awaitingClarification: counts.data.awaitingClarification,
      registrations: counts.data.pendingRegistrations,
      quarantine: quarantineCount.data?.count ?? 0,
      modifications: counts.data.pendingModificationRequests,
    }[key];
    return value > 0 ? value : undefined;
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-[var(--pv-rail-width)_minmax(0,1fr)]">
      {/* First tab stop on every page. */}
      <a href="#main" className="pv-skip-link">
        Skip to content
      </a>

      <nav
        data-pv-rail
        aria-label="Main navigation"
        className="sticky top-0 hidden h-screen flex-col gap-6 overflow-y-auto border-r border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-3.5 py-4 md:flex"
      >
        {/* The chamber leads. Provenir is a footnote at the bottom of this rail. */}
        <div className="flex items-center gap-2.5 px-1.5">
          {user.chamber?.logoUrl ? (
            <img
              src={user.chamber.logoUrl}
              alt={user.chamber.shortName}
              className="h-9 max-w-[180px] object-contain object-left"
            />
          ) : (
            <>
              <span className="grid size-9 shrink-0 place-items-center rounded-[var(--pv-radius)] bg-[var(--pv-brand)] [font-size:13px] font-bold text-[var(--pv-brand-on)]">
                {initials(user.chamber?.shortName ?? 'Provenir')}
              </span>
              <span className="min-w-0">
                <span className="block truncate [font-size:14px] font-bold leading-tight">
                  {user.chamber?.shortName ?? 'Provenir'}
                </span>
                <span className="pv-eyebrow block [font-size:9px] text-[var(--pv-faint)]">
                  Certificates of origin
                </span>
              </span>
            </>
          )}
        </div>

        {themeWarning && (
          <Tooltip content={themeWarning}>
            <button
              type="button"
              className="mx-1.5 flex items-start gap-2 rounded-[var(--pv-radius-sm)] bg-[var(--pv-warn-bg)] p-2 text-left [font-size:var(--pv-text-2xs)] leading-snug text-[var(--pv-warn)]"
            >
              <AlertTriangle aria-hidden className="mt-px size-3.5 shrink-0" />
              <span>This chamber's brand colour is being adjusted for readability.</span>
            </button>
          </Tooltip>
        )}

        <div className="flex flex-1 flex-col gap-5">
          {visibleSections.map((section) => (
            <div key={section.label}>
              <div className="pv-eyebrow mb-1.5 px-1.5 text-[var(--pv-faint)]">{section.label}</div>
              <ul className="flex list-none flex-col gap-px p-0">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-[var(--pv-radius-sm)] px-2.5 py-2 [font-size:13.5px] transition-colors',
                          isActive
                            ? 'bg-[var(--pv-brand)] font-semibold text-[var(--pv-brand-on)]'
                            : 'text-[var(--pv-ink-soft)] hover:bg-[rgba(20,48,30,0.06)] hover:text-[var(--pv-ink)]',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span className={cn('shrink-0', isActive ? 'opacity-100' : 'opacity-70')}>
                            {item.icon}
                          </span>
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                          {countFor(item.count) !== undefined && (
                            <span
                              className={cn(
                                'pv-num rounded-[var(--pv-radius-pill)] px-1.5 [font-size:10.5px] font-semibold',
                                isActive
                                  ? 'bg-[rgba(255,255,255,0.22)] text-[var(--pv-brand-on)]'
                                  : 'bg-[var(--pv-card-alt)] text-[var(--pv-ink-soft)]',
                              )}
                            >
                              {countFor(item.count)}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-[var(--pv-line-strong)] px-1.5 pt-3 [font-size:var(--pv-text-2xs)] leading-relaxed text-[var(--pv-faint)]">
          <p>Powered by Provenir</p>
          <p className="pv-num mt-0.5 text-[var(--pv-faint)]" title="The version of Provenir this page is running">
            Release {RELEASE_LABEL}
          </p>
        </div>
      </nav>

      <div className="flex min-w-0 flex-col">
        <header
          data-pv-topbar
          className="sticky top-0 z-[var(--pv-z-sticky)] flex h-[var(--pv-topbar-height)] items-center gap-3 border-b border-[var(--pv-line)] bg-[var(--pv-paper)]/95 px-5 backdrop-blur"
        >
          <Breadcrumb pathname={location.pathname} />
          <div className="flex-1" />

          {user.role === UserRole.PLATFORM_SUPPORT && (
            // Always on screen, so nobody forgets whose data they are in (D-031).
            <span className="flex items-center gap-2 [font-size:12.5px] text-[var(--pv-muted)]">
              {user.chamber ? (
                <>
                  Working in <b className="text-[var(--pv-ink)]">{user.chamber.shortName}</b>
                </>
              ) : (
                'No chamber chosen'
              )}
              <Button variant="secondary" size="sm" onClick={() => chooseChamber(null)}>
                {user.chamber ? 'Switch chamber' : 'Choose a chamber'}
              </Button>
            </span>
          )}
          {user.role === UserRole.PLATFORM_SUPER_ADMIN ? (
            <Badge tone="ink" size="sm">
              <ShieldCheck aria-hidden className="size-3" />
              Platform
            </Badge>
          ) : user.role === UserRole.PLATFORM_SUPPORT ? (
            <Badge tone="ink" size="sm">
              <LifeBuoy aria-hidden className="size-3" />
              Provenir Support
            </Badge>
          ) : (
            <Badge tone="neutral" size="sm">
              {user.role === UserRole.CHAMBER_ADMIN ? 'Chamber admin' : 'Operator'}
            </Badge>
          )}

          <Menu>
            <MenuTrigger asChild>
              <Button variant="ghost" size="sm" trailingIcon={<ChevronDown className="size-3.5" />}>
                <span className="grid size-6 place-items-center rounded-full bg-[var(--pv-brand-soft)] [font-size:10.5px] font-bold text-[var(--pv-ink)]">
                  {initials(`${user.firstName} ${user.lastName}`)}
                </span>
                <span className="hidden sm:inline">{user.firstName}</span>
              </Button>
            </MenuTrigger>
            <MenuContent>
              <MenuLabel>
                {user.firstName} {user.lastName}
              </MenuLabel>
              <div className="px-2.5 pb-2 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                {user.email}
              </div>
              <MenuSeparator />
              <MenuItem
                icon={<PenTool className="size-3.5" />}
                onSelect={() => navigate('/profile/signature')}
              >
                Signature profile
              </MenuItem>
              <MenuItem
                icon={<KeyRound className="size-3.5" />}
                onSelect={() => navigate('/profile/password')}
              >
                Change password
              </MenuItem>
              <MenuSeparator />
              <MenuItem icon={<LogOut className="size-3.5" />} onSelect={() => void signOut()}>
                Sign out
              </MenuItem>
            </MenuContent>
          </Menu>
        </header>

        <main id="main" className="min-w-0 flex-1 px-5 pb-16 pt-6">
          <div className="mx-auto w-full max-w-[var(--pv-content-max)]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Breadcrumbs derived from the path.
 *
 * Deliberately not a route-config lookup: the segments carry enough meaning for the two
 * or three levels this product has, and a parallel label registry is one more thing to
 * forget to update when a route is added.
 */
const SEGMENT_LABELS: Record<string, string> = {
  support: 'Choose a chamber',
  'support-officers': 'Support officers',
  documents: 'Documents',
  pending: 'Pending',
  processed: 'Processed',
  clarification: 'Awaiting clarification',
  registrations: 'Registrations',
  'support-desk': 'Find anything',
  trail: 'Trail',
  modifications: 'Modifications',
  configuration: 'Configuration',
  seals: 'Seal library',
  'required-documents': 'Required documents',
  stamping: 'Stamping',
  numbering: 'Certificate numbers',
  'master-data': 'Master data',
  users: 'Operators',
  platform: 'Platform',
  chambers: 'Chambers',
  exporters: 'Exporters',
  'document-types': 'Document types',
  integrators: 'Integrators',
  email: 'Email',
  mailbox: 'Mailbox',
  admins: 'Administrators',
  profile: 'Profile',
  signature: 'My signature',
  password: 'Change password',
};

function Breadcrumb({ pathname }: { pathname: string }) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return <span className="[font-size:13px] font-semibold">Dashboard</span>;
  }
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 [font-size:13px]">
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        // A cuid in the path is a record id, not a section — showing it would be noise.
        const label = SEGMENT_LABELS[segment] ?? (looksLikeId(segment) ? 'Detail' : segment);
        return (
          <span key={`${segment}-${index}`} className="flex min-w-0 items-center gap-1.5">
            {index > 0 && <span className="text-[var(--pv-faint)]">/</span>}
            <span
              className={cn(
                'truncate',
                isLast ? 'font-semibold text-[var(--pv-ink)]' : 'text-[var(--pv-muted)]',
              )}
            >
              {label}
            </span>
          </span>
        );
      })}
    </nav>
  );
}

function looksLikeId(value: string): boolean {
  return /^[a-z0-9]{20,}$/.test(value);
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}
